import type { Maybe } from '@nova/core';
import type { DebouncedSocketEvent } from './socketEventTypes';

/**
 * How a socket event relates to the entity a consumer is watching.
 *
 * `Collapsed` means the event was forwarded under another entity's id, so its payload describes that
 * other entity: the only safe response is to refetch, never to read fields off it.
 */
export enum SocketEventMatch {
  None = 'none',
  Direct = 'direct',
  Collapsed = 'collapsed'
}

export function matchSocketEventToEntity(
  event: Maybe<DebouncedSocketEvent>,
  entityId: Maybe<string>
): SocketEventMatch {
  if (!event || !entityId) {
    return SocketEventMatch.None;
  }

  if (event.id === entityId) {
    return SocketEventMatch.Direct;
  }

  return event.debounce?.ids?.includes(entityId)
    ? SocketEventMatch.Collapsed
    : SocketEventMatch.None;
}

/**
 * Whether the event swallowed other ids, regardless of which one it was forwarded under.
 *
 * For list consumers there is no single entity to match: any collapsed event may contain a row they
 * care about, and the payload only describes one of them — so they refetch rather than read it.
 */
export function hasCollapsedSocketEventIds(event: Maybe<DebouncedSocketEvent>): boolean {
  const ids = event?.debounce?.ids;

  return Array.isArray(ids) && ids.length > 1;
}
